import { Box, Button, CardMedia, Typography } from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import agua from "../../image/maiz.png";
import pasto from "../../image/pasto.png";
import { misionService } from "../../services/misionService";
import { setInformacion } from "../../store/slices/usuarios";
import { Alerta } from "../ux/Alert";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import { recolectarSevice } from "../../services/recolectarSevice";
import { tierras } from "../niveles/tierras";
import { Construccion } from "./Construccion";
import { SubirNivelButton } from "./botones/SubirNivelButton";
import { Contador } from "../comun/Contador";
import PetBenefitDisplay from "./PetBenefitDisplay";

dayjs.extend(utc);

/* =========================
   UI PRO FX (SOLO VISUAL)
   - NO cambia layout ni lógica
   ========================= */

const uiFx = {
  "@keyframes glowPulse": {
    "0%": { filter: "drop-shadow(0 0 0 rgba(255,215,0,0))" },
    "50%": { filter: "drop-shadow(0 0 12px rgba(255,215,0,.18))" },
    "100%": { filter: "drop-shadow(0 0 0 rgba(255,215,0,0))" },
  },
  "@keyframes floatSlow": {
    "0%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-6px)" },
    "100%": { transform: "translateY(0px)" },
  },
  "@keyframes shimmerBtn": {
    "0%": { transform: "translateX(-120%) skewX(-12deg)", opacity: 0 },
    "15%": { opacity: 0.22 },
    "35%": { opacity: 0.10 },
    "100%": { transform: "translateX(220%) skewX(-12deg)", opacity: 0 },
  },
  "@keyframes softFlicker": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.95 },
  },
  "@keyframes scanline": {
    "0%": { transform: "translateY(-120%)", opacity: 0 },
    "15%": { opacity: 0.16 },
    "50%": { opacity: 0.10 },
    "100%": { transform: "translateY(120%)", opacity: 0 },
  },
  // Partículas “polen” (corn vibe) — CSS puro
  "@keyframes pollenFall": {
    "0%": { transform: "translateY(-20px) translateX(0px)", opacity: 0 },
    "10%": { opacity: 0.26 },
    "100%": { transform: "translateY(520px) translateX(70px)", opacity: 0 },
  },
  "@keyframes pollenFall2": {
    "0%": { transform: "translateY(-30px) translateX(0px)", opacity: 0 },
    "10%": { opacity: 0.20 },
    "100%": { transform: "translateY(560px) translateX(-90px)", opacity: 0 },
  },
};

const rootFxSx = {
  ...uiFx,
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  filter: "saturate(1.06) contrast(1.02)",
  "&::before": {
    // iluminación/vignette suave
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(70% 60% at 50% 25%, rgba(255,215,0,0.08), transparent 60%)," +
      "radial-gradient(120% 90% at 50% 60%, rgba(0,0,0,0.18), rgba(0,0,0,0.52))",
    mixBlendMode: "screen",
    zIndex: 0,
  },
  "&::after": {
    // grain retro suave
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"160\" height=\"160\"><filter id=\"n\"><feTurbulence type=\"fractalNoise\" baseFrequency=\"0.8\" numOctaves=\"3\" stitchTiles=\"stitch\"/></filter><rect width=\"160\" height=\"160\" filter=\"url(%23n)\" opacity=\"0.22\"/></svg>')",
    opacity: 0.09,
    mixBlendMode: "overlay",
    zIndex: 0,
  },
};

const scanlineFxSx = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)",
    opacity: 0.22,
    animation: "scanline 5.2s linear infinite",
  },
};

const panelFxSx = {
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 18px 40px rgba(0,0,0,.30)",
  transform: "translateZ(0)",
  "&::after": {
    // brillo interior para profundidad
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(60% 45% at 50% 15%, rgba(255,255,255,0.06), transparent 55%)," +
      "linear-gradient(180deg, rgba(255,215,0,0.06), transparent 35%, rgba(0,0,0,0.14))",
  },
};

const invTextSx = {
  color: "white !important",
  fontSize: "0.7rem !important",
  textAlign: "start",
  mt: 1,
  textShadow: "0 0 10px rgba(0,0,0,.35)",
};

const titleSx = {
  display: "block",
  fontSize: "1.5rem !important",
  textAlign: "center !important",
  color: "white !important",
  textShadow:
    "0 0 10px rgba(255,255,255,0.16), 0 0 18px rgba(255,215,0,0.14), 0 8px 16px rgba(0,0,0,.22)",
  animation: "softFlicker 2.8s ease-in-out infinite",
  letterSpacing: "1px",
};

const lvlUpSx = {
  fontSize: "1rem !important",
  color: "greenyellow !important",
  textShadow: "0 0 14px rgba(173,255,47,.22)",
  animation: "glowPulse 3.4s ease-in-out infinite",
};

const btnFxSx = {
  position: "relative",
  overflow: "hidden",
  transition: "transform .12s ease, filter .12s ease",
  "&:hover": { transform: "translateY(-1px) scale(1.01)", filter: "brightness(1.08)" },
  "&:active": { transform: "translateY(0px) scale(0.98)", filter: "brightness(0.98)" },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "42%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.20), transparent)",
    animation: "shimmerBtn 2.9s ease-in-out infinite",
    pointerEvents: "none",
  },
};

// Partículas “polen” (no ocupa espacio, no mueve layout)
function PollenFX({ density = 12 }) {
  const items = useMemo(() => Array.from({ length: density }), [density]);

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>
      {items.map((_, idx) => {
        const left = Math.round(Math.random() * 100);
        const delay = (Math.random() * 4).toFixed(2);
        const dur = (4 + Math.random() * 4).toFixed(2);
        const size = 2 + Math.round(Math.random() * 4);
        const anim = idx % 2 === 0 ? "pollenFall" : "pollenFall2";
        return (
          <Box
            key={idx}
            sx={{
              position: "absolute",
              top: -40,
              left: `${left}%`,
              width: size,
              height: size,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(255,230,150,0.22), rgba(255,215,0,0.06))",
              boxShadow: "0 0 10px rgba(255,215,0,0.10)",
              opacity: 0,
              animation: `${anim} ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </Box>
  );
}

/* =========================
   COMPONENTE (MISMA LÓGICA)
   ========================= */

export const TierraMaiz = ({ selector, handleClose: handleClose2 }) => {
  const [semillas, setSemillas] = useState(1);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const { informacion } = useSelector((state) => state.userSlice);
  const dispatch = useDispatch();

  const handleClose = () => setOpen(false);
  const handleChange = (e) => {
    const cantidad = parseInt(e.target.value);
    if (cantidad > selector?.nivel) return;
    setSemillas(cantidad);
  };

  const recolectarSemilla = async () => {
    const { data } = await recolectarSevice(selector?.id);
    dispatch(setInformacion(data));
    setError(false);
    setMensaje("SUCCESS");
    setOpen(true);
  };

  const plantarSemilla = async () => {
    try {
      const { data } = await misionService(selector?.id, semillas);
      console.log(data);
      dispatch(setInformacion(data));
      setError(false);
      setMensaje("SUCCESS");
      setOpen(true);
    } catch (error) {
      setError(true);
      setOpen(true);
      const msg = error?.response?.data?.error || "ERROR";
      setMensaje(msg);
    }
  };

  // Solo visual: para animar el botón harvest cuando está listo (misma condición que ya usas)
  const canHarvest = dayjs().utc().diff(selector?.tiempo, "second") > 0;

  /* =========================
     ✅ NUEVO: IMÁGENES POR NIVEL + MENÚ + NEXT/LOCK + REQUISITOS + BOTÓN SOLO EN NEXT
     - NO toca lógica, solo UI del panel izquierdo
     ========================= */

  const CORN_LEVEL_IMAGES = useMemo(
    () => [
      // ✅ Pega aquí tus links por nivel (lvl1 → lvl5)
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767277907/pasto.b9b7aae7a28230fccbb6_pgkcfm.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278210/lvl2_rojyue.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278212/lvl3_zioeyi.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278212/lvl4_fhxhah.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278287/lvl5_c8ifrc.png",
    ],
    []
  );

  const landCfg = useMemo(() => tierras.find((t) => t.id === selector?.id), [selector?.id]);

  const [previewLevel, setPreviewLevel] = useState(selector?.nivel || 1);

  useEffect(() => {
    setPreviewLevel(selector?.nivel || 1);
  }, [selector?.id, selector?.nivel]);

  const maxLevel = CORN_LEVEL_IMAGES?.length || 5;
  const nextLevelReal = Math.min((selector?.nivel || 1) + 1, maxLevel);

  const reqForPreviewLevel = useMemo(() => {
    if (!landCfg?.nivel) return null;
    if (previewLevel <= 1) return null;
    const idx = previewLevel - 2; // lvl2->0, lvl3->1...
    return landCfg.nivel[idx] || null;
  }, [landCfg, previewLevel]);

  return (
    <Box sx={rootFxSx}>
      {/* FX overlays - NO cambian layout */}
      <PollenFX density={12} />
      <Box sx={scanlineFxSx} />

      {/* Tu layout intacto */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative !important",
          overflow: "auto !important",
          width: "100%",
          height: "100%",
          zIndex: 2,
        }}
      >
        <Construccion selector={selector} />

        <PetBenefitDisplay
          type="farm"
          icon="🌽"
          label="Corn production time"
        />

        {/* Close */}
        <Box
          onClick={handleClose2}
          sx={{
            position: "absolute",
            right: 14,
            top: 10,
            zIndex: 10001,
            color: "white",
            cursor: "pointer",
            fontSize: "14px",
            textShadow: "0 0 10px rgba(0,0,0,.7)",
            opacity: 0.85,
            transition: "all .12s ease",
            "&:hover": { opacity: 1, transform: "scale(1.06)" },
          }}
        >
          X
        </Box>

        <Box className="rpgui-container framed-golden" sx={{ position: "relative !important", ...panelFxSx }}>
          <Typography variant="p" sx={invTextSx}>
            Inventory:
          </Typography>
          <Typography variant="p" sx={invTextSx}>
            Corn:{" "}
            <span style={{ color: "#ffd27f" }}>
              {informacion?.inventario.find((i) => i?.item === "Corn")?.cantidad}
            </span>
          </Typography>
          <Typography variant="p" sx={invTextSx}>
            Water:{" "}
            <span style={{ color: "#7fe7ff" }}>
              {informacion?.inventario.find((i) => i?.item === "Water")?.cantidad}
            </span>
          </Typography>
          <Typography variant="p" sx={invTextSx}>
            Corn seed:{" "}
            <span style={{ color: "#d6ff6b" }}>
              {informacion?.inventario.find((i) => i?.item === "Corn seed")?.cantidad}
            </span>
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            flexDirection: { xs: "column", md: "row" },
            overflow: "auto !important",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            className="rpgui-container framed-golden-m"
            sx={{
              position: "relative !important",
              width: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexDirection: "column",
              height: "70vh",
              ...panelFxSx,
              pt: 2,
              gap: 1,
            }}
          >
            {/* ✅ Imagen grande del nivel seleccionado */}
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", px: 1 }}>
              <CardMedia
                className="rpgui-cursor-grab-open"
                component="img"
                image={CORN_LEVEL_IMAGES?.[Math.max(0, (previewLevel || 1) - 1)] || pasto}
                sx={{
                  width: "62%",
                  maxHeight: 260,
                  objectFit: "contain",
                  m: "0 auto",
                  animation: "floatSlow 3.4s ease-in-out infinite",
                  filter: "drop-shadow(0 16px 10px rgba(0,0,0,.30))",
                  userSelect: "none",
                }}
              />
            </Box>

            {/* ✅ Thumbs (niveles) */}
            <Box
              sx={{
                width: "100%",
                px: 1.5,
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 999,
                },
              }}
            >
              {Array.from({ length: maxLevel }).map((_, i) => {
                const lvl = i + 1;
                const isActive = lvl === previewLevel;
                const isOwned = lvl <= (selector?.nivel || 1);
                const isNext = lvl === nextLevelReal;

                return (
                  <Box
                    key={lvl}
                    onClick={() => setPreviewLevel(lvl)}
                    className="rpgui-cursor-point"
                    sx={{
                      minWidth: 64,
                      height: 54,
                      borderRadius: 1,
                      border: isActive ? "2px solid rgba(173,255,47,0.85)" : "1px solid rgba(255,255,255,0.18)",
                      boxShadow: isActive ? "0 0 18px rgba(173,255,47,0.18)" : "0 8px 18px rgba(0,0,0,0.22)",
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                      flex: "0 0 auto",
                      transform: isActive ? "translateY(-1px)" : "translateY(0px)",
                      transition: "all .12s ease",
                      "&:hover": { filter: "brightness(1.08)" },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={CORN_LEVEL_IMAGES?.[i] || pasto}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: isOwned ? 1 : 0.78,
                      }}
                    />

                    {/* Badge LVL */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 4,
                        top: 4,
                        px: 0.7,
                        py: 0.15,
                        borderRadius: 0.8,
                        background: "rgba(0,0,0,0.55)",
                        color: "white",
                        fontSize: "0.65rem",
                        textShadow: "0 0 10px rgba(0,0,0,.45)",
                      }}
                    >
                      LVL {lvl}
                    </Box>

                    {/* Estado */}
                    {isNext && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 4,
                          bottom: 4,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.8,
                          background: "rgba(173,255,47,0.18)",
                          border: "1px solid rgba(173,255,47,0.35)",
                          color: "greenyellow",
                          fontSize: "0.62rem",
                        }}
                      >
                        NEXT
                      </Box>
                    )}

                    {!isOwned && !isNext && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 4,
                          bottom: 4,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.8,
                          background: "rgba(0,0,0,0.45)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "rgba(255,255,255,0.85)",
                          fontSize: "0.62rem",
                        }}
                      >
                        LOCK
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* ✅ Requisitos + botón SOLO en NEXT */}
            <Box
              sx={{
                width: "100%",
                px: 2,
                pb: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.8,
              }}
            >
              <Typography variant="p" sx={lvlUpSx}>
                +LVL UP{" "}
                <div style={{ backgroundSize: "100%", width: "30px", height: "30px" }} className="rpgui-icon exclamation" />
              </Typography>

              {reqForPreviewLevel ? (
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="p"
                    sx={{
                      fontSize: "0.72rem !important",
                      color: "white !important",
                      textShadow: "0 0 10px rgba(0,0,0,.35)",
                      opacity: 0.9,
                    }}
                  >
                    Requisitos para llegar a{" "}
                    <span style={{ color: "#d6ff6b" }}>LVL {previewLevel}</span>:
                  </Typography>

                  {Object.entries(reqForPreviewLevel).map(([k, v]) => (
                    <Typography
                      key={k}
                      variant="p"
                      sx={{
                        fontSize: "0.7rem !important",
                        textAlign: "start",
                        color: "white !important",
                        textShadow: "0 0 10px rgba(0,0,0,.35)",
                      }}
                    >
                      {`${k}: ${v}`}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="p"
                  sx={{
                    fontSize: "0.72rem !important",
                    color: "rgba(255,255,255,0.85) !important",
                    textShadow: "0 0 10px rgba(0,0,0,.35)",
                    textAlign: "center",
                  }}
                >
                  Selecciona un nivel mayor para ver requisitos.
                </Typography>
              )}

              {previewLevel === nextLevelReal && nextLevelReal > (selector?.nivel || 1) ? (
                <Box sx={{ mt: 1, "& .rpgui-button": btnFxSx }}>
                  <SubirNivelButton setError={setError} setMensaje={setMensaje} setOpen={setOpen} selector={selector} />
                </Box>
              ) : (
                <Typography
                  variant="p"
                  sx={{
                    fontSize: "0.72rem !important",
                    color: "rgba(255,255,255,0.75) !important",
                    textShadow: "0 0 10px rgba(0,0,0,.35)",
                    textAlign: "center",
                  }}
                >
                  Para subir, selecciona el nivel{" "}
                  <span style={{ color: "#d6ff6b" }}>NEXT</span>.
                </Typography>
              )}
            </Box>

            {/* ✅ Mantengo tu CardMedia original (NO elimino nada),
                pero lo oculto para no duplicar UI */}
            <CardMedia
              className="rpgui-cursor-grab-open"
              component="img"
              image={pasto}
              sx={{
                width: 0,
                height: 0,
                opacity: 0,
                position: "absolute",
                pointerEvents: "none",
              }}
            />
          </Box>

          <Box
            className="rpgui-container framed-golden-m"
            sx={{
              position: "relative !important",
              ml: 1,
              width: "50%",
              height: "70vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              ...panelFxSx,
            }}
          >
            <Typography variant="p" sx={titleSx}>
              Corn Crop
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "start", width: "100%", height: "100%", flexDirection: "column" }}>
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", ml: 1 }}>
                <CardMedia
                  className="rpgui-cursor-grab-open"
                  component="img"
                  image={"https://res.cloudinary.com/devuvyuij/image/upload/v1681670270/CITYFARMING/assets%20wallet/image_u7r7pw.png"}
                  sx={{
                    width: "70px",
                    objectFit: "cover",
                    filter: "drop-shadow(0 16px 10px rgba(0,0,0,.30))",
                    animation: "glowPulse 3.0s ease-in-out infinite",
                  }}
                />
              </Box>

              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                LVL: <span style={{ color: "#d6ff6b" }}>{selector?.nivel}</span>
              </Typography>

              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                Production: <span style={{ color: "#ffd27f" }}>seed 1x2 Corn</span> 24 hours
              </Typography>

              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                Max seed: <span style={{ color: "#d6ff6b" }}>{selector?.nivel}</span>
              </Typography>

              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                Requirements:{" "}
                <span style={{ color: "#7fe7ff" }}>Water x {selector?.costo * parseInt(semillas)} seed</span>
              </Typography>

              {selector?.tiempo !== "" && (
                <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "center", color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                  harvest: <span style={{ color: "#ffd27f" }}>{selector?.semillas}</span>
                </Typography>
              )}

              {selector?.tiempo !== "" && (
                <Box sx={{ mt: 0.5, animation: canHarvest ? "glowPulse 1.8s ease-in-out infinite" : "softFlicker 2.8s ease-in-out infinite" }}>
                  <Contador segundos={selector?.tiempo} />
                </Box>
              )}

              {selector?.tiempo === "" && (
                <Box sx={{ mt: 1 }}>
                  <select value={semillas} onChange={handleChange} className="rpgui-dropdown ">
                    <option value="1">1 - seed</option>
                    <option value="2">2 - seed</option>
                    <option value="3">3 - seed</option>
                    <option value="4">4 - seed</option>
                  </select>
                </Box>
              )}

              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", ml: 1, mt: 1, gap: 1 }}>
                {selector?.tiempo === "" && (
                  <Button className="rpgui-button" onClick={plantarSemilla} sx={btnFxSx}>
                    <p>plant seeds</p>
                  </Button>
                )}

                {dayjs().utc().diff(selector?.tiempo, "second") > 0 && (
                  <Button
                    className="rpgui-button"
                    onClick={recolectarSemilla}
                    sx={{
                      ...btnFxSx,
                      animation: "glowPulse 1.8s ease-in-out infinite",
                    }}
                  >
                    <p>harvest</p>
                  </Button>
                )}
              </Box>
            </Box>

            <Alerta mensaje={mensaje} open={open} handleClose={handleClose} error={error} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
